import { useCallback, useMemo } from 'react'
import type { Creative, CreativeType, YouTubeShortsContent } from '../types'
import { fieldLengthState, platformContentViolations } from '../utils/validatePlatformContent'
import { useFlushPersistNow, useUpdateCreativeContent } from '../store/draft-creative-context'
import { FieldGroup } from '../components/FieldGroup'
import { useEditorHighlight } from '../hooks/useEditorHighlight'
import { VideoPickerBody } from './fields/VideoPickerBody'
import { HashtagInput } from './fields/HashtagInput'

const MAX_YOUTUBE_HASHTAGS = 15

interface YouTubeShortsEditorProps {
  creative: Creative<CreativeType.YouTubeShort>
}

export function YouTubeShortsEditor({ creative }: YouTubeShortsEditorProps) {
  const updateContent = useUpdateCreativeContent()
  const flushPersistNow = useFlushPersistNow()
  const { editorRef, highlightedSection, handleEditorBlur, setActiveSection } = useEditorHighlight(
    creative.id
  )
  const content = creative.content

  // Live preflight against the YouTube Data API v3 `videos` resource limits:
  // snippet.title 100 chars, snippet.description 5000 BYTES (not characters —
  // the old counter read `.length`, which is 3x optimistic for CJK), tags 500
  // chars combined. `<` and `>` are rejected outright in title and description.
  // The description counter includes the hashtag block, because that is what
  // YouTubePublishService actually uploads.
  const violationByField = useMemo(() => {
    const map = new Map<string, string>()
    for (const v of platformContentViolations({ type: creative.type, content })) {
      if (!map.has(v.contentField)) map.set(v.contentField, v.message)
    }
    return map
  }, [creative.type, content])

  const titleLength = fieldLengthState(creative.type, 'title', content)
  const descriptionLength = fieldLengthState(creative.type, 'description', content)

  const update = useCallback(
    (field: keyof YouTubeShortsContent, value: unknown) => {
      updateContent(creative.id, { [field]: value })
    },
    [creative.id, updateContent]
  )

  return (
    <div ref={editorRef} className="space-y-4" onBlurCapture={handleEditorBlur}>
      <FieldGroup label="Video" sectionId="video" highlighted={highlightedSection === 'video'}>
        <VideoPickerBody
          creativeId={creative.id}
          creativeType={creative.type}
          platform="youtube"
          selectedVideoAttachmentId={content.videoAttachmentId}
          selectedVideoUrl={content.videoUrl}
          onSelectVideo={(attachmentId, videoUrl, videoSource) => {
            updateContent(creative.id, { videoAttachmentId: attachmentId, videoUrl, videoSource })
            flushPersistNow(creative.id)
          }}
        />
      </FieldGroup>

      <FieldGroup
        label="Title"
        count={titleLength ? `${titleLength.count}/${titleLength.max}` : undefined}
        countOver={titleLength?.over}
        error={violationByField.get('title')}
        sectionId="title"
        highlighted={highlightedSection === 'title'}
      >
        <input
          type="text"
          value={content.title}
          onChange={e => update('title', e.target.value)}
          onFocusCapture={() => setActiveSection(creative.id, 'title')}
          placeholder="Enter a catchy title..."
          className="w-full rounded-lg border border-line-bright bg-bg-3 px-3 py-2 text-sm text-paper transition-colors outline-none placeholder:text-paper-faint focus:border-acid"
        />
      </FieldGroup>

      <FieldGroup
        label="Description"
        count={
          descriptionLength
            ? `${descriptionLength.count}/${descriptionLength.max} ${descriptionLength.unit}`
            : undefined
        }
        countOver={descriptionLength?.over}
        error={violationByField.get('description')}
        sectionId="description"
        highlighted={highlightedSection === 'description'}
      >
        <textarea
          value={content.description}
          onChange={e => update('description', e.target.value)}
          onFocusCapture={() => setActiveSection(creative.id, 'description')}
          placeholder="Write your description..."
          rows={4}
          className="w-full resize-none rounded-lg border border-line-bright bg-bg-3 px-3 py-2 text-sm text-paper transition-colors outline-none placeholder:text-paper-faint focus:border-acid"
        />
      </FieldGroup>

      <FieldGroup
        label={`Hashtags (${content.hashtags.length}/${MAX_YOUTUBE_HASHTAGS})`}
        count={content.hashtags.length >= MAX_YOUTUBE_HASHTAGS ? 'Limit reached' : undefined}
        error={violationByField.get('hashtags')}
        sectionId="hashtags"
        highlighted={highlightedSection === 'hashtags'}
      >
        <HashtagInput
          hashtags={content.hashtags}
          onChange={hashtags => update('hashtags', hashtags)}
          maxHashtags={MAX_YOUTUBE_HASHTAGS}
          onFocusCapture={() => setActiveSection(creative.id, 'hashtags')}
        />
      </FieldGroup>

      <FieldGroup
        label="Visibility"
        sectionId="visibility"
        highlighted={highlightedSection === 'visibility'}
      >
        <select
          value={content.visibility}
          onChange={e => update('visibility', e.target.value)}
          onFocusCapture={() => setActiveSection(creative.id, 'visibility')}
          className="w-full rounded-lg border border-line-bright bg-bg-3 px-3 py-2 text-sm text-paper transition-colors outline-none focus:border-acid"
        >
          <option value="public">Public</option>
          <option value="unlisted">Unlisted</option>
          <option value="private">Private</option>
        </select>
      </FieldGroup>
    </div>
  )
}
